// Quaternions in wxyz order; convert at the three.js boundary with toXYZW and nowhere else.

import * as vector3 from './vector3.ts'
import type { Vector3 } from './vector3.ts'

export type Quaternion = readonly [number, number, number, number]

export const IDENTITY: Quaternion = [1, 0, 0, 0]

export function multiply(first: Quaternion, second: Quaternion): Quaternion {
  const [aw, ax, ay, az] = first
  const [bw, bx, by, bz] = second
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ]
}

export function fromAxisAngle(axis: Vector3, radians: number): Quaternion {
  const unit = vector3.normalize(axis)
  const half = radians * 0.5
  const sine = Math.sin(half)
  return [Math.cos(half), unit[0] * sine, unit[1] * sine, unit[2] * sine]
}

// Minimal rotation taking unit `from` onto unit `to`; antiparallel picks any perpendicular.
export function fromUnitVectors(from: Vector3, to: Vector3): Quaternion {
  const start = vector3.normalize(from)
  const end = vector3.normalize(to)
  const alignment = vector3.dot(start, end)
  if (alignment > 1 - 1e-12) return IDENTITY
  if (alignment < -1 + 1e-12) {
    let axis = vector3.cross(start, [1, 0, 0])
    if (vector3.length(axis) < 1e-6) axis = vector3.cross(start, vector3.UP)
    const perpendicular = vector3.normalize(axis)
    return [0, perpendicular[0], perpendicular[1], perpendicular[2]]
  }
  const axis = vector3.cross(start, end)
  return normalize([1 + alignment, axis[0], axis[1], axis[2]])
}

export function normalize(quaternion: Quaternion): Quaternion {
  const [w, x, y, z] = quaternion
  const magnitude = Math.hypot(w, x, y, z) || 1
  return [w / magnitude, x / magnitude, y / magnitude, z / magnitude]
}

export function rotate(quaternion: Quaternion, vector: Vector3): Vector3 {
  const [w, x, y, z] = quaternion
  // v' = v + w(2 q_v x v) + q_v x (2 q_v x v)
  const crossX = 2 * (y * vector[2] - z * vector[1])
  const crossY = 2 * (z * vector[0] - x * vector[2])
  const crossZ = 2 * (x * vector[1] - y * vector[0])
  return [
    vector[0] + w * crossX + (y * crossZ - z * crossY),
    vector[1] + w * crossY + (z * crossX - x * crossZ),
    vector[2] + w * crossZ + (x * crossY - y * crossX),
  ]
}

export function toXYZW(quaternion: Quaternion): [number, number, number, number] {
  return [quaternion[1], quaternion[2], quaternion[3], quaternion[0]]
}

// pivot + R(quaternion)·vertex for a packed xyz array, reusing `out` when it fits.
export function rotateVertices(local: Float32Array, pivot: Vector3, quaternion: Quaternion,
                               out?: Float32Array): Float32Array {
  const vertices = out && out.length === local.length ? out : new Float32Array(local.length)
  const [w, x, y, z] = quaternion
  for (let vertex = 0; vertex < local.length / 3; vertex++) {
    const offset = vertex * 3
    const localX = local[offset], localY = local[offset + 1], localZ = local[offset + 2]
    const crossX = 2 * (y * localZ - z * localY)
    const crossY = 2 * (z * localX - x * localZ)
    const crossZ = 2 * (x * localY - y * localX)
    vertices[offset] = pivot[0] + localX + w * crossX + (y * crossZ - z * crossY)
    vertices[offset + 1] = pivot[1] + localY + w * crossY + (z * crossX - x * crossZ)
    vertices[offset + 2] = pivot[2] + localZ + w * crossZ + (x * crossY - y * crossX)
  }
  return vertices
}
